import type { TextDetection } from '../detect/pii/types';
import { loadPdfjs, loadPdfLib, type PdfPageInfo } from '../pipeline/pdf';
import { drawTextDetections, drawManualRects, canvasToPng } from '../lib/preview/render';
import type { NormalizedRect } from '../ui/types';

export type PageRedactPlan = {
  pageIndex: number;
  detections: readonly TextDetection[];
};

export type PdfRedactOptions = {
  /** Render scale; canvas pixels = pdf user units * scale. */
  scale?: number;
  /** Whether to clear the document info dictionary on the output. */
  stripMetadata?: boolean;
  /** User-drawn rectangles per page (normalized [0..1] of page viewport). */
  manualsByPage?: ReadonlyMap<number, readonly NormalizedRect[]>;
};

const RENDER_SCALE_DEFAULT = 2;

/**
 * Rasterize-and-flatten redaction. For each page:
 *   1. Render to canvas at `scale` DPR via pdfjs page.render()
 *   2. Draw black rectangles at every selected detection
 *   3. Encode PNG (lossless — keeps text edges crisp)
 *   4. Embed the PNG as a full-page image in a fresh pdf-lib doc
 * The original content stream is never copied. Underlying text and
 * fonts are gone. `pdftotext` on the output will not find redacted strings.
 */
export const redactPdf = async (
  pdfBytes: Uint8Array,
  pages: readonly PdfPageInfo[],
  selectionByPage: ReadonlyMap<number, readonly TextDetection[]>,
  opts: PdfRedactOptions = {},
): Promise<Blob> => {
  const scale = opts.scale ?? RENDER_SCALE_DEFAULT;
  const pdfjs = await loadPdfjs();
  const pdfLib = await loadPdfLib();

  const sourceDoc = await pdfjs.getDocument({ data: pdfBytes.slice() }).promise;
  const outDoc = await pdfLib.PDFDocument.create();

  // Always discard metadata in the output — that's the whole point.
  if (opts.stripMetadata !== false) {
    outDoc.setTitle('');
    outDoc.setAuthor('');
    outDoc.setSubject('');
    outDoc.setKeywords([]);
    outDoc.setProducer('anomia');
    outDoc.setCreator('anomia');
  }

  // One reusable canvas across all pages. Keeps peak memory at one page
  // worth of pixel buffer rather than `pageCount * pageBytes`, which is
  // what was spiking iPhone tabs to OOM.
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('redactPdf: 2D context unavailable');

  for (const pageInfo of pages) {
    const sourcePage = await sourceDoc.getPage(pageInfo.index + 1);
    const viewport = sourcePage.getViewport({ scale });
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await sourcePage.render({ canvas, canvasContext: ctx, viewport }).promise;

    const selected = selectionByPage.get(pageInfo.index) ?? [];
    drawTextDetections(ctx, pageInfo, selected, scale);

    const manuals = opts.manualsByPage?.get(pageInfo.index) ?? [];
    drawManualRects(ctx, manuals, canvas.width, canvas.height);

    const png = await canvasToPng(canvas);
    const pngBytes = new Uint8Array(await png.arrayBuffer());
    const embedded = await outDoc.embedPng(pngBytes);
    const newPage = outDoc.addPage([pageInfo.widthPdf, pageInfo.heightPdf]);
    newPage.drawImage(embedded, {
      x: 0,
      y: 0,
      width: pageInfo.widthPdf,
      height: pageInfo.heightPdf,
    });

    // Release pdfjs's per-page operator list / fonts / images. Without
    // this, multi-page PDFs accumulate retained native memory across the
    // whole loop.
    sourcePage.cleanup();
  }

  // Free the canvas backing store before the (potentially large) save().
  canvas.width = 0;
  canvas.height = 0;

  const out = await outDoc.save();
  // Pass the Uint8Array straight through to Blob — copying it into a
  // fresh ArrayBuffer here was momentarily doubling peak memory, which
  // pushed iPhone tabs over the OOM limit.
  return new Blob([out as BlobPart], { type: 'application/pdf' });
};

